import { Component, inject, OnChanges, SimpleChanges, ChangeDetectorRef, input, output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzTagComponent } from 'ng-zorro-antd/tag';
import {
  Message,
  MessageApiService,
  MessageThreadOpenedEvent,
  MessageThreadQuery,
  TaskStatisItem,
  MessageType,
  sumTaskFeedbackUnread,
} from '@app/message/message.api.service';
import { TaskAnalysis } from '@shared/model/task.models';
import { MessageListComponent } from '@app/message/components/message-list/message-list.component';
import {
  ReplyComposerComponent,
  ReplySendPayload,
  ReplyTarget,
} from '@app/message/components/reply/reply-composer.component';

/** 反馈切换器选项：一个反馈模块（task.moduleStatis 的一条） */
interface FeedbackOption {
  /** 切换器展示名 */
  label: string;
  /** 该选项对应的反馈模块 id（moduleStatis.moduleId），发送时回传 */
  moduleId: number;
  /** 已有会话才有的线程查询；无会话时 undefined，发送后由后端建 thread */
  threadQuery?: MessageThreadQuery;
}

/**
 * 任务级「反馈」tab（替代旧 fl-task-analysis-dicusses，走 /message/ 体系）
 *
 * - 用 getTaskStatis(taskId) 拿任务的 thread（题目 / 模块）
 * - 顶部模块切换器：模块列表以 task.moduleStatis 为准（无会话的模块也要有入口），
 *   按 moduleId 关联 statis 的已有会话；单模块任务额外兼容历史上 moduleId 为空的
 *   「任务级」会话。切换 moduleId → 展示对应模块的消息线程（无会话时为空态，发送后由后端建 thread）
 * - 内容为 fl-message-list（IM 式消息流）+ 底部 fl-reply-composer（回复具体对象/全班）
 */
@Component({
  selector: 'fl-task-feedback',
  templateUrl: './task-feedback.component.html',
  standalone: true,
  imports: [FormsModule, MessageListComponent, ReplyComposerComponent, NzTagComponent],
})
export class TaskFeedbackComponent implements OnChanges {
  /** 任务（含 moduleStatis 提供模块名） */
  readonly task = input<TaskAnalysis>(undefined);

  /** 任务消息未读数聚合（供宿主更新「任务反馈」tab 角标） */
  readonly unreadChange = output<number>();

  /** 全任务消息子话题统计（thread 列表） */
  statis: TaskStatisItem[] = [];

  /** 切换器选项（任务整体 + 各模块） */
  options: FeedbackOption[] = [];

  /** 当前选中下标 */
  activeIndex = 0;

  /** 回复成功后递增，触发 message-list 重拉 */
  refreshTick = 0;

  /** 回复器目标：`{}` 表示回复全班（常驻，无具体对象）；有 msgId 回复个人 */
  replyTarget: ReplyTarget = {};

  /** 清空信号：发送/关闭/切模块时递增，驱动常驻回复器清空输入 */
  clearTick = 0;

  /** 「同步全班」勾选状态 */
  toAll = false;

  private messageApiSrv = inject(MessageApiService);
  private messageSrv = inject(NzMessageService);
  private cdr = inject(ChangeDetectorRef);

  get activeOption(): FeedbackOption | undefined {
    return this.options[this.activeIndex];
  }

  get activeThreadQuery(): MessageThreadQuery | undefined {
    return this.activeOption?.threadQuery;
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['task']?.currentValue) {
      this.reload(changes['task'].currentValue);
    }
  }

  private async reload(task: TaskAnalysis) {
    if (!task?.id) {
      this.statis = [];
      this.options = [];
      return;
    }
    try {
      this.statis = await this.messageApiSrv.getTaskStatis(task.id);
    } catch {
      this.statis = [];
    }
    this.buildOptions();
    this.onCancelReply();
    // OnPush：任务反馈统计经 await 拉取后写入（statis 驱动切换器与消息流渲染），非模板事件需主动标记
    this.cdr.markForCheck();
  }

  /** 按 task.moduleStatis 铺全模块，并关联 statis 的已有会话，构造切换器选项 */
  private buildOptions() {
    const modules = this.task()?.moduleStatis ?? [];
    const moduleThreads = this.statis.filter(s => !s.paperQuestionId);
    const list: FeedbackOption[] = [];

    for (const m of modules) {
      // 反馈会话按模块的 moduleId 组织，moduleStatis.moduleId 与之同源；缺该字段的模块没有反馈入口
      const moduleId = m.moduleId;
      if (moduleId == null) continue;
      // 优先匹配 moduleId 相同的会话；单模块任务再兼容历史上 moduleId 为空的「任务级」会话
      const thread =
        moduleThreads.find(s => s.moduleId === moduleId) ??
        (modules.length === 1 ? moduleThreads.find(s => s.moduleId == null) : undefined);
      list.push({
        label: m.title || '模块',
        moduleId,
        threadQuery: thread
          ? {
              threadId: thread.id,
              taskId: this.task()?.id,
              paperQuestionId: undefined,
              moduleId,
            }
          : undefined,
      });
    }

    // 重拉（首次发送建会话）后按 moduleId 还原选中项，避免跳回第一个；首次进入或找不到则回到第一个
    const previous = this.options[this.activeIndex]?.moduleId;
    this.options = list;
    const restored = list.findIndex(opt => opt.moduleId === previous);
    this.activeIndex = restored >= 0 ? restored : 0;
    this.emitFeedbackUnread();
  }

  /** “任务反馈”tab 只统计任务级和模块级 thread，不混入题目反馈 */
  private emitFeedbackUnread() {
    this.unreadChange.emit(sumTaskFeedbackUnread(this.statis));
  }

  /** 切换模块 */
  onSelect(index: number) {
    if (index === this.activeIndex) return;
    this.activeIndex = index;
    // 切模块后清空残留回复器，避免把上一模块的回复发到别处
    this.onCancelReply();
  }

  /** 当前任务/模块反馈 thread 首屏获取成功后，只清除该 thread */
  onThreadOpened(event: MessageThreadOpenedEvent) {
    const item = this.statis.find(statis => statis.id === event.query.threadId);
    const clearedCount = Math.max(0, item?.unReadCount ?? 0);
    if (!item || clearedCount === 0) return;

    this.statis = this.statis.map(statis =>
      statis.id === event.query.threadId ? { ...statis, unReadCount: 0 } : statis
    );
    this.emitFeedbackUnread();
    this.messageApiSrv.consumeMessageUnread(clearedCount);
  }

  /** 点学生消息头像 → 唤起回复器，预填引用目标 */
  onAvatarClick(message: Message) {
    this.replyTarget = {
      msgId: message.id,
      firstName: message.firstName,
      content: message.content,
      messageType: message.messageType,
      avatar: message.avatar,
    };
    this.toAll = false;
  }

  /** 关闭回复器：常驻模式保留回复器渲染，退回全班态（replyTarget 置空对象 + clearTick 清空输入） */
  onCancelReply() {
    this.replyTarget = {};
    this.toAll = false;
    this.clearTick += 1;
  }

  /** 点「同步全班」勾选：回复全班时它只作提示（消息本就全班可见），吞掉点击避免勾选框被切换 */
  onToAllClick(event: MouseEvent) {
    if (!this.replyTarget?.msgId) event.preventDefault();
  }

  /** 发送回复：有 msgId 走 replyMessage（toAll 控制是否同步全班），无则对当前模块 createMessage */
  async onSendReply(payload: ReplySendPayload) {
    const target = this.replyTarget;
    const option = this.activeOption;
    const query = option?.threadQuery;
    // 回复具体对象只需 msgId；回复全班需当前模块（模块未加载出来时不发，避免退化成无 moduleId 的发送）。
    // 不判 !target：全班态 replyTarget 为空对象也放行
    const task = this.task();
    if (!target?.msgId && (!task?.id || !option)) return;
    if (!payload.content && payload.mediaUrls.length === 0) return;
    try {
      if (target.msgId) {
        await this.messageApiSrv.replyMessage(target.msgId, {
          content: payload.content,
          toAll: this.toAll,
          mediaUrls: payload.mediaUrls,
        });
      } else {
        await this.messageApiSrv.createMessage({
          taskId: task?.id,
          paperQuestionId: query?.paperQuestionId ?? null,
          moduleId: option?.moduleId ?? null,
          content: payload.content,
          mediaUrls: payload.mediaUrls,
          type: MessageType.Default,
        });
      }
      if (query) {
        this.refreshTick += 1;
      } else {
        // 当前模块尚无会话：重拉 statis 让新会话出现在切换器与消息流（refreshTick 只驱动已渲染的消息流）
        await this.reload(task!);
      }
      this.onCancelReply();
      // OnPush：发送成功后刷新计数/清空信号在 await 续体写入，非模板事件需主动标记
      this.cdr.markForCheck();
    } catch (e: any) {
      this.messageSrv.error(e?.error?.detail ?? '发送失败，请重试');
    }
  }
}
